import { LANGUAGE, PROVIDER_TYPE } from '@prisma/client';
import { add, set } from 'date-fns';
import { signOut } from 'next-auth/react';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types/fields';

import { cast } from '@/util/util';

const baseUrl = process.env.NEXTAUTH_URL ? `https://${process.env.NEXTAUTH_URL}` : 'http://localhost:3000';
const callBackUrl = `${baseUrl}${getSignInRedirectPath()}`;

export function getContainerName(language: LANGUAGE): string {
  if (language == LANGUAGE.CPP) {
    return 'cpp';
  }

  return 'nasm';
}
export async function signOutAndRedirect(): Promise<void> {
  console.log('Signing out and redirecting to: ' + callBackUrl);
  await signOut({ callbackUrl: callBackUrl });
}

const redirectUrlKey = 'prevPath';
export function setRedirectUrl(url: string) {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(redirectUrlKey, url);
  }
}
export function removeRedirectUrl(): void {
  sessionStorage.removeItem(redirectUrlKey);
}

export function getRedirectUrl(): string | null {
  return typeof window !== 'undefined' ? sessionStorage.getItem(redirectUrlKey) : null;
}

export function getSignInRedirectPath(callbackUrl?: string): string {
  if (callbackUrl) {
    return `/app/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`;
  }

  return '/app/auth/signin';
}

export function getAuthenticatedHomePath(): string {
  return '/app/org';
}

export function getRepositoryUrl(type: PROVIDER_TYPE, repoOrg: string, repoName: string): string {
  switch (type) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    case PROVIDER_TYPE.GITHUB:
      return `https://github.com/${repoOrg}/${repoName}`;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    case PROVIDER_TYPE.GITLAB:
      return `https://gitlab.com/${repoOrg}/${repoName}`;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    case PROVIDER_TYPE.BITBUCKET:
      return `https://bitbucket.org/${repoOrg}/${repoName}`;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    case PROVIDER_TYPE.AZUREDEVOPS:
      return `https://dev.azure.com/${repoOrg}/${repoName}`;
    default:
      return '';
  }
}

export function getTypeFromUrl(type: PROVIDER_TYPE, url: string): PROVIDER_TYPE {
  if (url.includes('github.com')) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
    return PROVIDER_TYPE.GITHUB;
  } else if (url.includes('gitlab.com')) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
    return PROVIDER_TYPE.GITLAB;
  } else if (url.includes('bitbucket.org')) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
    return PROVIDER_TYPE.BITBUCKET;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
    return PROVIDER_TYPE.AZUREDEVOPS;
  }
}

export function removeKeyImmutable<T extends Record<string, unknown>>(obj: T, key: keyof T) {
  const clone = { ...obj };
  delete clone[key];
  return clone;
}

export function addKeyImmutable<T extends Record<string, T[keyof T]>>(obj: T, key: keyof T, value: T[keyof T]) {
  const clone = { ...obj };
  clone[key] = value;
  return clone;
}

export function removeKeysImmutable<T extends Record<string, unknown>>(obj: T, keys: (keyof T)[]) {
  const clone = { ...obj };
  for (const key of keys) {
    delete clone[key];
  }

  return clone;
}

export function pipeLog<T>(val: T, label?: string) {
  if (label) {
    console.log(label, val);
  } else {
    console.log(val);
  }
  return val;
}

export function immutableSort<T>(arr: T[], compareFn?: ((a: T, b: T) => number) | undefined) {
  return [...arr].sort(compareFn);
}
export const makeReduceToRecord =
  <T, K extends keyof any, V>(getKey: (val: T) => K, getVal: (val: T) => V) =>
  (dictionary: Record<K, V>, item: T) => ({
    ...dictionary,
    [getKey(item)]: getVal(item)
  });

export function reduceToRecord<T, K extends keyof any>(values: T[], getKey: (val: T) => K): Record<K, T>;
export function reduceToRecord<T, K extends keyof any, V>(values: T[], getKey: (val: T) => K, getVal: (val: T) => V): Record<K, V>;
export function reduceToRecord<T, K extends keyof any, V = T>(values: T[], getKey: (val: T) => K, getVal?: (val: T) => V) {
  if (getVal === undefined) {
    return values.reduce<Record<K, T>>(
      makeReduceToRecord(getKey, i => i),
      {} as Record<K, T>
    );
  }
  return values.reduce<Record<K, V>>(makeReduceToRecord(getKey, getVal), {} as Record<K, V>);
}

export async function showUserFailure(action: () => Promise<unknown> | unknown, errorMessage = 'Failed to perform action.') {
  try {
    await Promise.resolve(action());
  } catch (e) {
    throw errorMessage;
    console.error(e);
  }
}

export function useFormSubmission<T extends (values: TFormValues) => Promise<void> | void, TFormValues extends FieldValues>(
  action: T,
  form: UseFormReturn<TFormValues>,
  errorMessage = 'Failed to perform action. Please try again later...'
) {
  const { setError } = form;
  type TReturnFn = (...args: Parameters<T>) => Promise<void>;
  return useCallback(
    (async (values: TFormValues) => {
      try {
        return await action(values);
      } catch (e) {
        setError('root.serverError', {
          type: 'server',
          message: errorMessage
        });
        console.error(e);
      }
    }) as TReturnFn,
    [action, errorMessage, setError]
  );
}

export function teacherifyTime(date: Date): Date {
  const val = set(date, {
    hours: 23,
    minutes: 59
  });

  return val;
}

export function getOpenTime(): Date {
  return set(new Date(), {
    hours: 23,
    minutes: 59
  });
}

export function getCloseTime(date: Date): Date {
  return set(
    add(date, {
      months: 1
    }),
    {
      hours: 23,
      minutes: 59
    }
  );
}

export function getDueTime(date: Date): Date {
  return set(
    add(date, {
      weeks: 2
    }),
    {
      hours: 23,
      minutes: 59
    }
  );
}

export enum IDEType {
  INTELLIJ = 'IntelliJ',
  WEBSTORM = 'WebStorm',
  PYCHARM = 'PyCharm',
  CLION = 'CLion',
  GOLAND = 'GoLand',
  DATAGRIP = 'DataGrip',
  APPCODE = 'AppCode',
  RUBYMINE = 'RubyMine',
  PHPSTORM = 'PhpStorm',
  RIDER = 'Rider',
  DATASPELL = 'DataSpell',
  ANDROIDSTUDIO = 'AndroidStudio'
}

export function getIDE(lang: LANGUAGE) {
  switch (lang) {
    case LANGUAGE.JAVA:
      return IDEType.INTELLIJ;
    case LANGUAGE.JAVASCRIPT:
      return IDEType.WEBSTORM;
    case LANGUAGE.TYPESCRIPT:
      return IDEType.WEBSTORM;
    case LANGUAGE.PYTHON:
      return IDEType.PYCHARM;
    case LANGUAGE.CPP:
      return IDEType.CLION;
    case LANGUAGE.GO:
      return IDEType.GOLAND;
    case LANGUAGE.PYTHON_JUPYTER:
      return IDEType.DATASPELL;
    case LANGUAGE.R:
      return IDEType.PYCHARM; // Special requires plugin
    case LANGUAGE.KOTLIN:
      return IDEType.INTELLIJ;
    case LANGUAGE.KOTLIN_JUPYTER:
      return IDEType.DATASPELL;
    case LANGUAGE.KOTLIN_MOBILE:
      return IDEType.ANDROIDSTUDIO;
    case LANGUAGE.FSHARP:
      return IDEType.RIDER;
    case LANGUAGE.CSHARP:
      return IDEType.RIDER;
    case LANGUAGE.ASSEMBLY:
      return IDEType.CLION;
    case LANGUAGE.MATLAB: // fallthrough as we currently don't support matlab
    default:
      return IDEType.INTELLIJ;
  }
}

export enum osType {
  WINDOWS,
  MAC,
  LINUX
}
